/**
 * Converts an HTMLImageElement to a data URL
 * @param {HTMLImageElement} image - The image element to convert
 * @returns {string|null} Data URL representation of the image, or null on error
 */
export const convertImageToDataUrl = (image) => {
  if (!image) return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width || 100;
    canvas.height = image.naturalHeight || image.height || 100;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0);
    return canvas.toDataURL("image/png", 0.8);
  } catch (err) {
    return null;
  }
};

/**
 * Converts a video source URL to a data URL
 * @param {string} videoSrc - The video source URL (can be data URL, blob URL, or regular URL)
 * @returns {Promise<string|null>} Data URL representation of the video, or original URL on error
 */
export const convertVideoToDataUrl = async (videoSrc) => {
  if (!videoSrc) return null;
  try {
    // If it's already a data URL, return it
    if (videoSrc.startsWith("data:")) {
      return videoSrc;
    }
    // If it's a blob URL, convert it to data URL
    if (videoSrc.startsWith("blob:")) {
      const response = await fetch(videoSrc);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(videoSrc); // Fallback to original
        reader.readAsDataURL(blob);
      });
    }
    return videoSrc;
  } catch (err) {
    return videoSrc; // Fallback to original
  }
};

/**
 * Generates a thumbnail from a video source
 * @param {string} videoSrc - The video source URL
 * @param {number} width - Thumbnail width (default: 320)
 * @param {number} height - Thumbnail height (default: 180)
 * @returns {Promise<string|null>} Thumbnail as data URL, or null on error
 */
export const generateVideoThumbnail = async (
  videoSrc,
  width = 320,
  height = 180
) => {
  if (!videoSrc) return null;

  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      // Set video to first second
      video.currentTime = 1;
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        // Draw video frame to canvas
        ctx.drawImage(video, 0, 0, width, height);

        // Convert to data URL
        const thumbnailDataUrl = canvas.toDataURL("image/png", 0.8);
        resolve(thumbnailDataUrl);
      } catch (err) {
        resolve(null);
      }
    };

    video.onerror = () => {
      resolve(null);
    };

    // Timeout after 5 seconds
    setTimeout(() => {
      resolve(null);
    }, 5000);

    video.src = videoSrc;
  });
};
